import type { Ref } from 'react';
import { forwardRef, useCallback } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { GrayOutlineButton } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { useObjectAccessState, useUserObjectAccess } from '@/containers/ObjectAccess/hooks';
import UserObjectAccessContainer from '@/containers/ObjectAccess/UserObjectAccessContainer';
import type { WorkspaceObjectRole } from '@/enums';
import { WorkspaceRoleMap } from '@/enums';
import type { ObjectAccessUser } from '@/types/workspace.access';
import { cx } from '@trinity/chat-components';
import styles from './style/UserAccessContextMenu.module.css';

type MenuProps = {
  onClose: () => void;
};

export const UserActionsContextMenu = forwardRef(({ onClose }: MenuProps, ref: Ref<HTMLDivElement>) => {
  const [_, dispatch] = useObjectAccessState();
  const { roles, userId } = useUserObjectAccess();

  const removeUser = useCallback(() => {
    dispatch({
      type: 'remove',
      userId,
    });
  }, [
    dispatch,
    userId,
  ]);

  const updateUserRole = useCallback((roleId: WorkspaceObjectRole) => () => {
    dispatch({
      type: 'update-role',
      roleId,
      userId,
    });
    onClose();
  }, [
    onClose,
    dispatch,
    userId,
  ]);

  return (
    <PopperMenu className={styles.menu} ref={ref}>
      {roles.map(role => (
        <PopperMenuItem key={role} onClick={updateUserRole(role)}>
          {WorkspaceRoleMap[role]}
        </PopperMenuItem>
      ))}
      <div className={styles.divider} />
      <PopperMenuItem
        onClick={removeUser}>
        Remove
      </PopperMenuItem>
    </PopperMenu>
  );
});

type Props = {
  item: ObjectAccessUser;
};

export const UserObjectAccessPopper = (props: Props) => {

  return (
    <UserObjectAccessContainer
      selectedRole={props.item.roleId}
      userId={props.item.user.id}>
      <PopupState variant="popper" popupId="user-object-access-popper">
        {popupState => (
          <div>
            <div {...bindToggle(popupState)}>
              <GrayOutlineButton
                onClick={popupState.open}
                className={cx(styles.roleBtn, { [styles.open]: popupState.isOpen })}>
                <div>{WorkspaceRoleMap[props.item.roleId]}</div>
                <div className={styles.chevron} />
              </GrayOutlineButton>
            </div>
            <Popper
              {...bindPopper(popupState)}
              placement="bottom-end"
              className={styles.popper}>
              <ClickAwayListener
                onClickAway={() => popupState.close()}>
                <UserActionsContextMenu
                  onClose={popupState.close} />
              </ClickAwayListener>
            </Popper>
          </div>
        )}
      </PopupState>
    </UserObjectAccessContainer>
  );
};

export default UserObjectAccessPopper;